import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.assign.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.promise.js";
import "regenerator-runtime/runtime.js";

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }

function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }

import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import isEqual from 'lodash.isequal';
import { fetchImage } from '@anewgo/utils'; //
//
// Generic
//

export var useWindowSize = function useWindowSize() {
  var isClient = typeof window === 'object';

  function getSize() {
    return {
      width: isClient ? window.innerWidth : undefined,
      height: isClient ? window.innerHeight : undefined
    };
  }

  var _useState = useState(getSize),
      windowSize = _useState[0],
      setWindowSize = _useState[1];

  useEffect(function () {
    if (!isClient) {
      return false;
    }

    function handleResize() {
      setWindowSize(getSize());
    }

    window.addEventListener('resize', handleResize);
    return function () {
      return window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty array ensures that effect is only run on mount and unmount

  return windowSize;
};
export var useResizeObserver = function useResizeObserver(ref) {
  var _useState2 = useState(0),
      width = _useState2[0],
      setWidth = _useState2[1];

  var _useState3 = useState(0),
      height = _useState3[0],
      setHeight = _useState3[1];

  useLayoutEffect(function () {
    var current = ref.current;
    var resizeObserver = new ResizeObserver(function (entries) {
      var _entries$0$contentRec = entries[0].contentRect,
          width = _entries$0$contentRec.width,
          height = _entries$0$contentRec.height;
      setWidth(width);
      setHeight(height);
    });

    if (current !== null) {
      resizeObserver.observe(current);
    }

    return function () {
      resizeObserver.disconnect();
    };
  }, [ref]);
  return [width, height];
};

var AsyncFetchImage = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee(url, setImg, mountedRef, abortSignal) {
    var img;
    return regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return fetchImage(url, abortSignal);

          case 2:
            img = _context.sent;

            if (mountedRef.current && !(abortSignal && abortSignal.aborted)) {
              // Only run this setter if the fetch wasn't aborted.
              setImg(img);
            }

          case 4:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function AsyncFetchImage(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}(); // allows for an image to be fetched asynchronously as a side effect
// image is stored to a component's state with the setImg function passed in.
// mountedRef is created with useRef and is set to true if a component is
// mounted, and set to false if a component is no longer mounted.
// if the image is unmounted, mountedRef will be false. In that case, don't
// call setImg. Also, prevent race conditions by aborting earlier fetches and
// setters if newer calls to useImageFetch are made.


export var useImageFetch = function useImageFetch(url, setImg, mountedRef) {
  // Heavily borrowed from https://sebastienlorber.com/handling-api-request-race-conditions-in-react
  // Store abort controller, in order to be able to cancel the most recently requested image fetch.
  var abortControllerRef = useRef();
  useEffect(function () {
    // When issuing a new request, first cancel the previous request.
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    } // Create a new AbortController for the new request, and store it in the ref.


    var currentAbortController = new AbortController();
    abortControllerRef.current = currentAbortController;

    if (url) {
      // Fetch an image, and run setter.
      // This might be aborted by a subsequent newer request.
      AsyncFetchImage(url, setImg, mountedRef, currentAbortController.signal);
    }
  }, [url, setImg, mountedRef] // if url changes, fetch again
  );
}; // Like useImageFetch, but with better encapsulation

export var useImage = function useImage(url) {
  var mountedRef = useRef();

  var _useState4 = useState(),
      img = _useState4[0],
      setImg = _useState4[1];

  useEffect(function () {
    mountedRef.current = true;
    return function () {
      return mountedRef.current = false;
    };
  }, []);
  useImageFetch(url, setImg, mountedRef);
  return img;
}; // Performs an effect the first time a ref is assigned. Ensures that a render
// has already happened before performing the effect.

export var useRefEffect = function useRefEffect(effect, deps) {
  var hasRendered = useRef(false);
  var rootRef = useRef();
  useEffect(function () {
    hasRendered.current = true;

    if (rootRef.current) {
      effect();
    }
  }, deps);

  var assignRef = function assignRef(instance) {
    if (instance && rootRef.current !== instance) {
      rootRef.current = instance;

      if (hasRendered.current) {
        effect();
      }
    }
  };

  return [rootRef, assignRef];
};
export var useWhyDidYouUpdate = function useWhyDidYouUpdate(name, props) {
  // Get a mutable ref object where we can store props ...
  // ... for comparison next time this hook runs.
  var previousProps = useRef();
  useEffect(function () {
    if (previousProps.current) {
      // Get all keys from previous and current props
      var allKeys = Object.keys(Object.assign({}, previousProps.current, props)); // Use this object to keep track of changed props

      var changesObj = {}; // Iterate through keys

      allKeys.forEach(function (key) {
        // If previous is different from current
        if (!isEqual(previousProps.current[key], props[key])) {
          // Add to changesObj
          changesObj[key] = {
            from: previousProps.current[key],
            to: props[key]
          };
        }
      }); // If changesObj not empty then output to console

      if (Object.keys(changesObj).length) {
        console.log('[why-did-you-update]', name, changesObj);
      }
    } // Finally update previousProps with current props for next hook call


    previousProps.current = props;
  });
};
export var usePrevious = function usePrevious(value) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  var ref = useRef(); // Store current value in ref

  useEffect(function () {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)

  return ref.current;
};
export var useTitle = function useTitle(_ref2) {
  var title = _ref2.title;
  useEffect(function () {
    document.title = title;
  }, [title]);
};
export var useMetaTags = function useMetaTags(_ref3) {
  var name = _ref3.name,
      property = _ref3.property,
      content = _ref3.content;
  useEffect(function () {
    var elements = document.getElementsByTagName('meta');

    for (var i = 0; i < elements.length; i++) {
      var element = elements.item(i);

      if (name && element.getAttribute('name') === name) {
        element.content = content;
        break;
      } else if (property && element.getAttribute('property') === property) {
        element.content = content;
        break;
      }
    }
  }, [name, property, content]);
};
export var useIntersect = function useIntersect(_ref4) {
  var _ref4$root = _ref4.root,
      root = _ref4$root === void 0 ? null : _ref4$root,
      rootMargin = _ref4.rootMargin,
      _ref4$threshold = _ref4.threshold,
      threshold = _ref4$threshold === void 0 ? 0 : _ref4$threshold;

  var _useState5 = useState({}),
      entry = _useState5[0],
      updateEntry = _useState5[1];

  var _useState6 = useState(null),
      node = _useState6[0],
      setNode = _useState6[1];

  var observer = useRef(null);
  useEffect(function () {
    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new window.IntersectionObserver(function (_ref5) {
      var entry = _ref5[0];
      return updateEntry(entry);
    }, {
      root: root,
      rootMargin: rootMargin,
      threshold: threshold
    });
    var currentObserver = observer.current;

    if (node) {
      currentObserver.observe(node);
    }

    return function () {
      return currentObserver.disconnect();
    };
  }, [node, root, rootMargin, threshold]);
  return [setNode, entry];
};